import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import {
	fetchLocations,
	setLocationSearchCriteres,
} from "../../store/actions/locationSearchActions"
import moment from "moment"
import { PROPRE_VEHICULE_TYPE_LOCATION } from "../../utilities/Addons"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"

const EMPTY_SEARCH = { nom: "", vehLoc: 0, dateT: 0, dateD: "", dateF: "" }

const DATE_TYPES = [
	{ value: 0, label: "Location" },
	{ value: 1, label: "Restitution" },
]

// The store/back-end speak DD-MM-YYYY; native <input type="date"> speaks
// YYYY-MM-DD. Convert at the boundary so the search payload stays unchanged.
const toInputDate = (d) =>
	d ? moment(d, "DD-MM-YYYY").format("YYYY-MM-DD") : ""
const fromInputDate = (d) =>
	d ? moment(d, "YYYY-MM-DD").format("DD-MM-YYYY") : ""

// Formulaire de recherche location (V2). Page 1/2 : la saisie des critères. Au
// clic « Rechercher », on mémorise les critères, on lance la recherche puis on
// navigue vers la page de résultats (`/location/resultats`) — même flux que
// l'atelier depuis le 31.07.2026 (la page unique formulaire + tableau avec son
// toggle `hideResult` a disparu).
const LocationSearch = () => {
	const dispatch = useDispatch()
	const history = useHistory()

	const masterVehicules = useSelector((s) => s.masterReducer.masterVehicules)
	const fetchAutreEntites = useSelector((s) => s.autreInfo.fetchAutreEntites)
	const criteres = useSelector((s) => s.locationSearchInfo.criteres)
	const fetchLocationFiches = useSelector(
		(s) => s.locationSearchInfo.fetchLocationFiches,
	)

	// Préremplissage depuis la dernière recherche (les critères sont stockés
	// tels qu'envoyés à l'API, dates en DD-MM-YYYY).
	const [search, setSearch] = useState(() => ({
		...EMPTY_SEARCH,
		...(criteres || {}),
	}))

	// Capture the value synchronously: React 16 pools synthetic events, so
	// reading e.target inside the async state updater would throw.
	const updateSearch = (field, e) => {
		const value = e.target.value
		setSearch((prev) => ({ ...prev, [field]: value }))
	}

	const reset = () => setSearch({ ...EMPTY_SEARCH })

	const runSearch = () => {
		dispatch(setLocationSearchCriteres(search))
		dispatch(fetchLocations(search)).then((json) => {
			if (json) history.push("/location/resultats")
		})
	}

	const enterSearch = (e) => {
		if (e.key === "Enter") runSearch()
	}

	// F9 = réinitialiser (raccourci historique de la page). L'impression F11
	// vit désormais sur la page de résultats, avec le tableau.
	useEffect(() => {
		const onKeyDown = (e) => {
			if (e.key === "F9") {
				e.preventDefault()
				reset()
			}
			if (e.keyCode === 121) e.preventDefault()
		}
		document.addEventListener("keydown", onKeyDown)
		return () => document.removeEventListener("keydown", onKeyDown)
	}, [])

	return (
		<div style={gs.page}>
			<div style={styles.formShell}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Location</div>
						<div style={gs.title}>Rechercher une fiche</div>
					</div>
				</div>
				<div style={gs.card}>
					<div style={gs.fieldGroup}>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Nom</label>
							<input
								style={gs.input}
								type="text"
								value={search.nom}
								onChange={(e) => updateSearch("nom", e)}
								onKeyUp={enterSearch}
							/>
						</div>

						<div style={gs.fieldRow}>
							<label style={gs.label}>Véhicule de location</label>
							{fetchAutreEntites.loading ? (
								<div style={{ ...gs.input, ...styles.syncRow }}>
									<FontAwesomeIcon icon={faSync} spin />
								</div>
							) : (
								<select
									style={gs.select}
									value={search.vehLoc}
									onChange={(e) => updateSearch("vehLoc", e)}
									onKeyUp={enterSearch}
								>
									<option value={0}>-</option>
									{masterVehicules
										.filter(
											(v) =>
												v.propre_vehicule.type ===
												PROPRE_VEHICULE_TYPE_LOCATION,
										)
										.map((v) => (
											<option
												key={`vehLoc${v.id}`}
												value={v.id}
											>
												{`${v.marque} ${v.modele}`}
											</option>
										))}
								</select>
							)}
						</div>

						<div style={gs.fieldRow}>
							<label style={gs.label}>Date</label>
							<div style={styles.segmented}>
								{DATE_TYPES.map((opt) => {
									const active =
										Number(search.dateT) === opt.value
									return (
										<button
											key={opt.value}
											type="button"
											style={styles.segment(active)}
											onClick={() =>
												setSearch((prev) => ({
													...prev,
													dateT: opt.value,
												}))
											}
										>
											{opt.label}
										</button>
									)
								})}
							</div>
						</div>

						<div style={gs.fieldRow}>
							<label style={gs.label}>Du</label>
							<input
								style={gs.input}
								type="date"
								value={toInputDate(search.dateD)}
								onChange={(e) => {
									const v = e.target.value
									setSearch((prev) => ({
										...prev,
										dateD: v ? fromInputDate(v) : "",
									}))
								}}
								onKeyUp={enterSearch}
							/>
						</div>

						<div style={gs.fieldRow}>
							<label style={gs.label}>Au</label>
							<input
								style={gs.input}
								type="date"
								value={toInputDate(search.dateF)}
								min={toInputDate(search.dateD) || undefined}
								onChange={(e) => {
									const v = e.target.value
									setSearch((prev) => ({
										...prev,
										dateF: v ? fromInputDate(v) : "",
									}))
								}}
								onKeyUp={enterSearch}
							/>
						</div>
					</div>
				</div>
				<div style={{ ...gs.footer, marginTop: 20 }}>
					<button type="button" style={gs.btnSecondary} onClick={reset}>
						Réinitialiser
					</button>
					<button type="button" style={gs.btnPrimary} onClick={runSearch}>
						{fetchLocationFiches.loading ? (
							<FontAwesomeIcon icon={faSync} spin />
						) : (
							"Rechercher"
						)}
					</button>
				</div>
			</div>
		</div>
	)
}

const styles = {
	formShell: {
		width: "100%",
		maxWidth: 640,
		margin: "0 auto",
	},
	segmented: {
		display: "inline-flex",
		gap: 2,
		padding: 4,
		backgroundColor: COLORS.bg,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
	},
	segment: (active) => ({
		padding: "7px 16px",
		borderRadius: 7,
		border: "none",
		fontFamily: "inherit",
		fontSize: 13,
		fontWeight: 500,
		cursor: "pointer",
		whiteSpace: "nowrap",
		color: active ? COLORS.text : COLORS.textMuted,
		backgroundColor: active ? COLORS.surface : "transparent",
		boxShadow: active ? "0 1px 3px rgba(28, 25, 23, 0.08)" : "none",
		transition: "color 0.12s, background-color 0.12s",
	}),
	syncRow: {
		display: "flex",
		justifyContent: "center",
		padding: "8px 0",
		color: COLORS.textMuted,
	},
}

export default LocationSearch
